import { useCallback, useState } from 'react';
import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation, useTRPCQuery } from '../../../../../trpc/trpc';

/** One OAuth (MCP) client the user approved for the active workspace (`oauthClients.list`). */
export interface ConnectedApp {
  clientId: string;
  clientName: string;
  redirectHost: string | null;
  firstApprovedAt: Date | string;
  lastUsedAt: Date | string | null;
  scopes: string[];
}

/** The connected-apps list of Settings → AI assistants, and revoking one app. */
export function useConnectedApps() {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();

  const {
    data,
    isLoading,
    error,
    refetch: refetchUnsafe,
  } = useTRPCQuery(trpcAPI.oauthClients.list.queryOptions(undefined, { retry: false }));

  const refetch = useCallback(() => {
    refetchUnsafe().catch(console.error);
  }, [refetchUnsafe]);

  const [revokingClientId, setRevokingClientId] = useState<string | null>(null);
  const revokeMutation = useTRPCMutation({
    mutationFn: (input: { clientId: string }) => trpcClient.oauthClients.revoke.mutate(input),
    onSuccess: () => {
      setRevokingClientId(null);
      refetch();
    },
    onError: () => {
      setRevokingClientId(null);
      // The app may be gone already (another tab, or its tokens expired). Show the list as it is now.
      refetch();
    },
  });

  const revokeApp = useCallback(
    (clientId: string) => {
      setRevokingClientId(clientId);
      revokeMutation.mutate({ clientId });
    },
    [revokeMutation]
  );

  return {
    apps: (data as ConnectedApp[] | undefined) ?? [],
    isLoading,
    error,
    refetch,
    revokeApp,
    revokingClientId,
    revokeError: revokeMutation.error,
  };
}
